import React, { useState } from 'react'
import {
  Form,
  Input,
  Select,
  Button,
  DatePicker,
  Radio,
  Switch,
  Row,
  Col,
  Space,
  Modal,
  Descriptions,
  Tooltip,
  Tabs,
  Popover,
  message
} from 'antd'
import { ErrorBoundary } from 'react-error-boundary'
import { QuestionCircleOutlined, SmileOutlined } from '@ant-design/icons'
import {
  AndroidPushNotificationPreview,
  ApplePushNotificationPreview
} from 'push-notification-preview'
import {
  SEND_APP_NOTIFICATION,
  PREVIEW_CAMPAIGN_CUSTOMER_DEVICE_TOKENS
} from './AppSettingsQueries'
import { QUERY_LOCATIONS } from './../Locations/LocationQueries'
import { useQuery, useLazyQuery, useMutation } from '@apollo/client'
import { useSlerp } from '@slerp/client'
import moment from 'moment'
import { CalendarIcon } from '@slerp/assets'
import { TextFieldNumber, SelectPrefixWrapper } from './../Widgets'
import { nonEmptySelectRule, requiredRule } from './Rules'
import data from '@emoji-mart/data'
import Picker from '@emoji-mart/react'

const { RangePicker } = DatePicker
const dateFormat = 'DD/MM/YYYY'
const timeFormat = 'HH:mm'
const dateTimeFormat = 'DD/MM/YYYY HH:mm'
const queryDateFormat = 'YYYY-MM-DD'

export type StoreType = {
  name: string
  id: string
  archived_at?: string | null
}

export type CustomerType = {
  id: string
  first_name: string
  last_name: string
  email: string
}

type NotificationFormValuesType = {
  store_ids: string[]
  title: string
  notification_title: string
  notification_body: string
  has_birthday: boolean
  has_orders: boolean
  has_stamps: boolean
  has_unredeemed_rewards: boolean
  count: number
}

const SendNotifications = ({ setDrawerVisibility, refetch }) => {
  const { user } = useSlerp()
  const [stores, setStores] = useState<Array<StoreType>>([])
  const [selectStores, setSelectStores] = useState<Array<string>>([])

  useQuery(QUERY_LOCATIONS, {
    variables: { merchantSlug: user?.merchant?.slug },
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const valid = data.stores.filter((store: StoreType) => !store.archived_at)
      setStores(valid)
      setSelectStores(valid)
    }
  })

  const [form] = Form.useForm()

  const [startDate, setStartDate] = useState<moment.Moment>(
    moment().subtract(7, 'days')
  )
  const [endDate, setEndDate] = useState<moment.Moment>(moment())
  const [publishNow, setPublishNow] = useState(true)
  const [publishOn, setPublishOn] = useState<moment.Moment>(moment())
  const [sendToAll, setSendToAll] = useState(true)
  const [queryCriterion, setQueryCriterion] = useState('')
  const [inclusionQuery] = useState(true)
  const [count, setCount] = useState(1)
  const [queryByVolume, setQueryByVolume] = useState(false)
  const [disableSubmit, setDisableSubmit] = useState(false)
  const [allStoresEnabled, setAllStoresEnabled] = useState(true)
  const [previewVisible, setPreviewVisible] = useState(false)

  const [sendAppNotification] = useMutation(SEND_APP_NOTIFICATION, {
    fetchPolicy: 'no-cache'
  })
  const [previewCampaignCustomerDeviceTokens, { data: previewData }] =
    useLazyQuery(PREVIEW_CAMPAIGN_CUSTOMER_DEVICE_TOKENS, {
      fetchPolicy: 'no-cache',
      onCompleted: () => {
        message.destroy()
        message.success('Preview created!')
      },
      onError: () => {
        message.destroy()
        message.error('Something went wrong!')
      }
    })

  const previewCampaign = (values: NotificationFormValuesType) => {
    setDisableSubmit(true)
    message.loading('Creating campaign preview')
    form
      .validateFields()
      .then(() => {
        const inclusion =
          queryCriterion === 'birthdays' ||
          queryCriterion === 'unredeemed_rewards'
            ? true
            : inclusionQuery

        const variables = {
          merchantId: user?.merchant?.id,
          storeIds: !allStoresEnabled
            ? selectStores
            : stores.map((store: StoreType) => store.id),
          sendToAll,
          hasOrdered: queryCriterion === 'orders',
          hasStamped: queryCriterion === 'stamps',
          hasBirthday: queryCriterion === 'birthdays',
          hasUnredeemedRewards: queryCriterion === 'unredeemed_rewards',
          exception: !inclusion,
          startDate: startDate.format(queryDateFormat),
          endDate: endDate.format(queryDateFormat)
        }

        const queryParameters = queryByVolume
          ? {
              ...variables,
              count: count
            }
          : variables

        previewCampaignCustomerDeviceTokens({ variables: queryParameters })
        setPreviewVisible(true)
        setDisableSubmit(false)
      })
      .catch(() => {
        message.destroy()
        message.error('Please address the errors and try again!')
      })
  }

  const onFinish = (values: NotificationFormValuesType) => {
    setDisableSubmit(true)
    message.loading('Sending notifications')
    form
      .validateFields()
      .then(() => {
        const { title, notification_title, notification_body } = values
        const inclusion =
          queryCriterion === 'birthdays' ||
          queryCriterion === 'unredeemed_rewards'
            ? true
            : inclusionQuery

        const variables = {
          merchantId: user?.merchant?.id,
          storeIds: !allStoresEnabled
            ? selectStores
            : stores.map((store: StoreType) => store.id),
          title,
          notificationTitle: notification_title,
          notificationBody: notification_body,
          hasOrdered: queryCriterion === 'orders',
          hasStamped: queryCriterion === 'stamps',
          hasUnredeemedRewards: queryCriterion === 'unredeemed_rewards',
          hasBirthday: queryCriterion === 'birthdays',
          exception: !inclusion,
          startDate: startDate.format(queryDateFormat),
          endDate: endDate.format(queryDateFormat),
          sendToAll,
          isPublished: publishNow,
          publishOn: publishOn.toISOString()
        }

        const mutationParameters = queryByVolume
          ? {
              ...variables,
              count: count
            }
          : variables

        sendAppNotification({ variables: mutationParameters })
          .then(() => {
            message.destroy()
            if (publishNow) {
              message.success('Notifications sent!')
            } else {
              message.success(
                `Notifications scheduled for ${publishOn.format(
                  dateTimeFormat
                )}!`
              )
            }
          })
          .catch(() => {
            message.destroy()
            message.error('Something went wrong!')
          })
      })
      .finally(() => {
        setDisableSubmit(false)
        setDrawerVisibility(false)
        refetch()
      })
  }

  const changeDates = (dates: Array<moment.Moment>) => {
    const [changedStartDate, changedEndDate] = dates

    setStartDate(changedStartDate)
    setEndDate(changedEndDate)
  }

  const showStoreSection =
    queryCriterion === 'orders' || queryCriterion === 'stamps'
  const [showDateSection, setShowDateSection] = useState(false)

  const checkStoreFieldValue = () => {
    const storeIds = form.getFieldValue('store_ids') || []
    const isDateSectionShown = storeIds.length > 0

    setSelectStores(storeIds)
    setShowDateSection(isDateSectionShown)
  }

  const handleCancel = () => {
    setPreviewVisible(false)
  }

  const handleOk = () => {
    setPreviewVisible(false)
    form.submit()
  }

  const range = (start: number, end: number) => {
    const result = []
    for (let i = start; i < end; i++) {
      result.push(i)
    }
    return result
  }

  const disabledDate = (current) => {
    return moment().add(-1, 'days') > current
  }

  const disabledTime = (current) => {
    const now = moment()

    if (current.format('YYYY-MM-DD') === now.format('YYYY-MM-DD')) {
      const hours = range(0, now.hour())
      const minutes =
        current.hour() === now.hour() ? range(0, now.minute()) : []

      return {
        disabledHours: () => hours,
        disabledMinutes: () => minutes
      }
    }
  }

  const previewModalFooter = [
    <Button key='back' type='ghost' onClick={handleCancel}>
      Cancel
    </Button>,
    <Button
      key='submit'
      type='primary'
      disabled={disableSubmit}
      onClick={handleOk}
    >
      Create
    </Button>
  ]

  const handleEmojiSelect = (event, form, name) => {
    const emojiNative = event.native
    const val = form.getFieldValue(name) || ''

    form.setFieldsValue({
      [name]: val.concat(event.native)
    })
  }

  const emojiPicker = (form, name) => {
    return (
      <Picker
        data={data}
        theme='light'
        onEmojiSelect={(e) => handleEmojiSelect(e, form, name)}
      />
    )
  }

  return (
    <>
      <Row>
        <Col span={24}>
          <Form
            className='_mt-8'
            form={form}
            data-testid='app-settinsg-form'
            onFinish={onFinish}
            layout='vertical'
            initialValues={{ count: 1 }}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 20 }}
          >
            <Form.Item
              label='Campaign title'
              name='title'
              required
              rules={requiredRule}
              className='_mb-16'
            >
              <Input placeholder='January Offers' />
            </Form.Item>
            <Form.Item
              label='Notification title'
              name='notification_title'
              rules={requiredRule}
              required
              className='_mb-16'
            >
              <Input
                placeholder='20% Off Tonight!'
                suffix={
                  <Popover
                    placement='bottomRight'
                    content={emojiPicker(form, 'notification_title')}
                  >
                    <SmileOutlined />
                  </Popover>
                }
              />
            </Form.Item>
            <Form.Item label='Notification body' rules={requiredRule} required>
              <Row>
                <Col span='23'>
                  <Form.Item
                    name='notification_body'
                    rules={requiredRule}
                    required
                    noStyle
                  >
                    <Input.TextArea placeholder='Use code JAN20 to get 20% off tonight!' />
                  </Form.Item>
                </Col>
                <Col>
                  <Popover
                    placement='bottomRight'
                    content={emojiPicker(form, 'notification_body')}
                  >
                    <SmileOutlined className='_ml-8' />
                  </Popover>
                </Col>
              </Row>
            </Form.Item>
            <Form.Item name='publishNow'>
              <Row align='middle' className='_mb-0 _pt-4 _pb-4'>
                <Col>
                  <span>Schedule for later</span>
                </Col>
                <Col>
                  <Switch
                    className='_ml-12'
                    checked={!publishNow}
                    onChange={() => {
                      setPublishNow(!publishNow)
                    }}
                  />
                </Col>
              </Row>
            </Form.Item>
            {!publishNow && (
              <React.Fragment>
                <Form.Item
                  name='publishOn'
                  label='Send notifications on'
                  className='_mt-24'
                >
                  <SelectPrefixWrapper prefixicon={<CalendarIcon />}>
                    <DatePicker
                      format={dateTimeFormat}
                      allowClear={false}
                      defaultValue={publishOn}
                      onChange={(value: any) => {
                        setPublishOn(value)
                      }}
                      className='_pl-48'
                      showTime={{ format: timeFormat }}
                      disabledDate={disabledDate}
                      disabledTime={disabledTime}
                    />
                  </SelectPrefixWrapper>
                </Form.Item>
              </React.Fragment>
            )}
            <Form.Item name='send_to_all'>
              <Row align='middle' className='_mb-0 _pt-4 _pb-4'>
                <Col>
                  <span>Send to all customers</span>
                </Col>
                <Col>
                  <Switch
                    className='_ml-12'
                    checked={sendToAll}
                    onChange={() => {
                      setSendToAll(!sendToAll)
                    }}
                  />
                </Col>
              </Row>
            </Form.Item>
            {!sendToAll && (
              <React.Fragment>
                <Form.Item
                  rules={requiredRule}
                  label='Send to customers based on:'
                  name='criteria'
                >
                  <Radio.Group
                    optionType={'button'}
                    buttonStyle={'solid'}
                    onChange={(e: Event) => {
                      setQueryCriterion(e.target.value)
                    }}
                  >
                    <Space direction='vertical' align='start'>
                      <Radio value={'orders'}>
                        When and where they ordered
                      </Radio>
                      <Radio value={'stamps'}>
                        When and where they collected loyalty stamps
                      </Radio>
                      <Radio value={'unredeemed_rewards'}>
                        Unredeemed rewards collected
                      </Radio>
                      <Radio value={'birthdays'}>Birthday</Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
                {showStoreSection && (
                  <StoreSection
                    {...{
                      allStoresEnabled,
                      setAllStoresEnabled,
                      queryCriterion,
                      stores,
                      inclusionQuery,
                      checkStoreFieldValue
                    }}
                  />
                )}
                {(showDateSection || allStoresEnabled) &&
                  queryCriterion !== 'unredemeed_rewards' && (
                    <DateSection
                      {...{ startDate, endDate, changeDates, queryCriterion }}
                    />
                  )}
                {queryCriterion !== 'birthdays' && inclusionQuery && (
                  <QueryByVolumeSection
                    {...{
                      queryCriterion,
                      queryByVolume,
                      setQueryByVolume,
                      count,
                      setCount
                    }}
                  />
                )}
              </React.Fragment>
            )}
          </Form>
          <Row justify='end' className='_mt-32'>
            <Button type='primary' onClick={previewCampaign}>
              Preview Campaign
            </Button>
          </Row>
        </Col>
      </Row>
      <Modal
        visible={previewVisible}
        footer={previewModalFooter}
        onOk={handleOk}
        onCancel={handleCancel}
        closable={false}
      >
        {previewData && (
          <>
            <h2>Campaign Summary</h2>
            <Descriptions layout='vertical' column={1}>
              <Descriptions.Item
                style={{ paddingBottom: '4px' }}
                contentStyle={{ paddingBottom: '16px' }}
                label='Campaign Title'
              >
                {form.getFieldValue('title')}
              </Descriptions.Item>
              <Descriptions.Item
                style={{ paddingBottom: '4px' }}
                contentStyle={{ paddingBottom: '16px' }}
                label='Notification Title'
              >
                {form.getFieldValue('notification_title')}
              </Descriptions.Item>
              <Descriptions.Item
                style={{ paddingBottom: '4px' }}
                contentStyle={{ paddingBottom: '16px' }}
                label='Notification Body'
              >
                {form.getFieldValue('notification_body')}
              </Descriptions.Item>
              <Descriptions.Item
                style={{ paddingBottom: '4px' }}
                contentStyle={{ paddingBottom: '16px' }}
                label={
                  <>
                    Audience Size
                    <Tooltip
                      title='This represents the total number of guests or customers who have the potential to receive your push notifications. Please note that only those who have downloaded your app will be included in this number.'
                      placement='right'
                    >
                      <QuestionCircleOutlined className='_ml-8' />
                    </Tooltip>
                  </>
                }
              >
                {previewData.previewCampaignCustomerDeviceTokens.length}
              </Descriptions.Item>
            </Descriptions>

            <h2>
              Preview
              <Tooltip
                title='Please note that the notification layout may differ based on the users operating system version and screensize.'
                placement='right'
              >
                <QuestionCircleOutlined className='_ml-8' />
              </Tooltip>
            </h2>
            <Tabs destroyInactiveTabPane={true}>
              <Tabs.TabPane tab='Android' key='1'>
                <Col>
                  <ErrorBoundary
                    fallback={<p>Unable to render Android preview.</p>}
                  >
                    <div style={{ backgroundColor: '#f3f2f4', padding: '2px' }}>
                      <AndroidPushNotificationPreview
                        appName={user?.merchant?.name}
                        title={form.getFieldValue('notification_title')}
                        message={form.getFieldValue('notification_body')}
                      />
                    </div>
                  </ErrorBoundary>
                </Col>
              </Tabs.TabPane>
              <Tabs.TabPane tab='iOS' key='2'>
                <Col style>
                  <ErrorBoundary
                    fallback={<p>Unable to render iOS preview.</p>}
                  >
                    <div style={{ backgroundColor: '#f3f2f4', padding: '2px' }}>
                      <ApplePushNotificationPreview
                        appName={user?.merchant?.name}
                        title={form.getFieldValue('notification_title')}
                        message={form.getFieldValue('notification_body')}
                      />
                    </div>
                  </ErrorBoundary>
                </Col>
              </Tabs.TabPane>
            </Tabs>
          </>
        )}
      </Modal>
    </>
  )
}

type StoreSectionProps = {
  allStoresEnabled: boolean
  setAllStoresEnabled: (arg0: boolean) => void
  inclusionQuery: boolean
  stores: StoreType[]
  queryCriterion: string
  checkStoreFieldValue: () => void
}

const StoreSection = ({
  allStoresEnabled,
  setAllStoresEnabled,
  inclusionQuery,
  stores,
  queryCriterion,
  checkStoreFieldValue
}: StoreSectionProps) => {
  const storeLabel =
    queryCriterion === 'orders'
      ? 'What stores did they order from'
      : 'What stores did they stamp from'
  return (
    <React.Fragment>
      <Form.Item>
        <Row align='middle' className='_mb-0 _pt-4 _pb-4'>
          <Col>
            <span>Send to all stores</span>
          </Col>
          <Col>
            <Switch
              className='_ml-12'
              checked={allStoresEnabled}
              onChange={() => {
                setAllStoresEnabled(!allStoresEnabled)
                checkStoreFieldValue()
              }}
            />
          </Col>
        </Row>
      </Form.Item>
      {!allStoresEnabled && (
        <Form.Item
          label={storeLabel}
          name='store_ids'
          rules={nonEmptySelectRule}
        >
          <Select
            mode='multiple'
            placeholder='Stores'
            showSearch
            onChange={() => checkStoreFieldValue()}
            filterOption={(input, option) =>
              option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {stores.map(({ id, name }: StoreType) => (
              <Select.Option value={id} key={id}>
                {name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}
    </React.Fragment>
  )
}

type DateSectionProps = {
  startDate: moment.Moment
  endDate: moment.Moment
  changeDates: (arg0: Array<moment.Moment>) => void
  queryCriterion: string
}

const DateSection = ({
  startDate,
  endDate,
  changeDates,
  queryCriterion
}: DateSectionProps) => {
  const orderOrStampLabel =
    queryCriterion === 'orders'
      ? 'Dates orders made between'
      : 'Dates stamps made between'

  const dateLabel =
    queryCriterion === 'birthdays' ? 'Birthday between' : orderOrStampLabel

  return (
    <Form.Item name='date' label={dateLabel} className='_mt-24  '>
      <SelectPrefixWrapper prefixicon={<CalendarIcon />}>
        <RangePicker
          format={dateFormat}
          //fix this any shit
          allowClear={false}
          onChange={(e: any) => changeDates(e)}
          placeholder={['Start date', 'End date']}
          defaultValue={[startDate, endDate]}
          className='_pl-48'
        />
      </SelectPrefixWrapper>
    </Form.Item>
  )
}

type VolumeSectionProps = {
  queryCriterion: string
  queryByVolume: boolean
  setQueryByVolume: (arg0: boolean) => void
  count: integer
  setCount: (arg0: integer) => void
}

const QueryByVolumeSection = ({
  queryCriterion,
  queryByVolume,
  setQueryByVolume,
  count,
  setCount
}: VolumeSectionProps) => {
  const orderOrStampLabel =
    queryCriterion === 'orders'
      ? 'Send only to customers with the most orders'
      : 'Send only to customers with the most stamps'
  const volumeLabel =
    queryCriterion === 'unredeemed_rewards'
      ? 'Send only to customers with the most unredeemed rewards'
      : orderOrStampLabel
  return (
    <Col>
      <Row align='middle' className='_mb-0 _mt-24'>
        <Col className='_mb-8'>
          <span>{volumeLabel}</span>
          <Switch
            className='_ml-12'
            checked={queryByVolume}
            onChange={() => {
              setQueryByVolume(!queryByVolume)
            }}
          />
        </Col>
      </Row>
      {queryByVolume && (
        <Col>
          <Form.Item rules={requiredRule} name='count'>
            <TextFieldNumber
              className='_ml-12'
              inputType='number'
              min={1}
              max={999}
              step={1}
              style={{ maxWidth: '100%' }}
              onChange={(e) => setCount(e)}
            />
          </Form.Item>
        </Col>
      )}
    </Col>
  )
}

export default SendNotifications
